import { Box, Chip, Container, Grid, Skeleton, Stack, Typography, useTheme } from '@mui/material';
import update from 'immutability-helper';
import { filter, find, get, isEmpty, map, size } from 'lodash';
import queryString from 'query-string';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

// Components
import ButtonWithLoading from 'src/components/common/ButtonWithLoading';
import StyledPagination from 'src/components/common/StyledPagination';
import { SectionErrorBoundary } from 'src/components/ErrorBoundary/SectionErrorBoundary';
import FormBookmarkNote from 'src/features/Bookmark/FormBookmarkNote';
import CompanyProfileCard from 'src/features/CompanyProfiles/CompanyProfileCard';
import CompanyProfileDetail from 'src/features/CompanyProfiles/CompanyProfileDetail';
import FilterCompanyProfile from 'src/features/CompanyProfiles/Filter';
import NoDataSearchCompany from 'src/features/CompanyProfiles/NoDataSearchCompany';
import { useQueryParams } from 'src/hooks/useQueryParam';
import { BookmarkTypes } from 'src/model/bookmark';
import { useMutateCreateBookmark, useMutateUpdateBookmarkByDataUuid } from 'src/services/mutate/useMutateBookmark';
import { useQueryListCompany } from 'src/services/queries/useQueryCompany';
import { LIMIT_GET_LIST } from 'src/utils/constants/config';
import { CompanyStatusCode } from 'src/utils/constants/enum';
import { AuthRouteKeys } from 'src/utils/constants/routes';
import { SUCCESS_CODE } from 'src/utils/constants/statusCode';

const CompanyProfilePage = () => {
  const query = useQueryParams();
  const getCompanyPayload = { ...query };

  if (getCompanyPayload.include_all_company_status === 'true') {
    // query string returns string type not boolean
    getCompanyPayload.statuses_code = [
      CompanyStatusCode.Success,
      CompanyStatusCode.Error,
      CompanyStatusCode.Warning,
      CompanyStatusCode.Unknown,
    ];
  }
  delete getCompanyPayload.include_all_company_status;

  if (!isEmpty(getCompanyPayload.company_name)) {
    delete getCompanyPayload.sort_by;
  }
  const [, setSearchParams] = useSearchParams();

  const [isBookmarkList, setIsBookmarkList] = useState<Record<string, boolean>>({}); // Fake update bookmark in FE
  const [noteBookmarkList, setNoteBookmarkList] = useState<Record<string, string>>({}); // Fake update bookmark in FE
  /**  When choose company to create/update data bookmark => Store to this state */
  const [bookmarkNoteForm, setBookmarkNoteForm] = useState<{
    isOpen: boolean;
    companyId: string;
    companyBookmarkNote: string;
    companyHasBookmark: boolean;
  }>({ isOpen: false, companyId: '', companyBookmarkNote: '', companyHasBookmark: false });
  const { mutateAsync: createBookmarkMutate, isPending: isPendingCreateBookmark } = useMutateCreateBookmark();
  const { mutateAsync: updateBookmarkMutate, isPending: isPendingUpdateBookmark } = useMutateUpdateBookmarkByDataUuid();
  const { data, isFetching } = useQueryListCompany(queryString.stringify(getCompanyPayload));
  const { data: listCompany = [] } = data;
  const [selected, setSelected] = useState(undefined as undefined | ICompanyProfile);
  const [selectedCompanies, setSelectedCompanies] = useState([] as ICompanyProfile[]);
  const navigate = useNavigate();

  useEffect(() => {
    const initNoteBookmarkList = listCompany.reduce((acc, company) => {
      acc = {
        ...acc,
        [company.id]: company?.bookmark_note || '',
      };
      return acc;
    }, {});
    setNoteBookmarkList(initNoteBookmarkList);
  }, [listCompany]);

  const checkCompanyIsBookmark = (company: ICompanyProfile) => {
    // Kiểm tra cả dữ liệu trả về từ server và state dùng để fake update trên FE
    return company.has_bookmark || get(isBookmarkList, company.id || '', false);
  };

  const updateNoteBookmark = (note: string) => {
    setNoteBookmarkList((prev) => {
      const newNoteBookmarkList = update(prev, {
        [bookmarkNoteForm?.companyId || '']: {
          $set: note,
        },
      });
      return newNoteBookmarkList;
    });
  };

  const handleOpenBookmarkNoteForm = (companyId: string, companyHasBookmark: boolean) => {
    setBookmarkNoteForm({
      isOpen: true,
      companyId,
      companyBookmarkNote: get(noteBookmarkList, companyId, ''),
      companyHasBookmark,
    });
  };

  const handleCloseBookmarkNoteForm = () => {
    setBookmarkNoteForm({
      isOpen: false,
      companyId: '',
      companyBookmarkNote: '',
      companyHasBookmark: false,
    });
  };

  const handleSubmitBookmarkNoteForm = async () => {
    const response = bookmarkNoteForm?.companyHasBookmark
      ? await updateBookmarkMutate({
          type: BookmarkTypes.Company,
          dataUuid: bookmarkNoteForm?.companyId || '',
          body: {
            note: get(noteBookmarkList, bookmarkNoteForm.companyId, ''),
          },
        })
      : await createBookmarkMutate({
          type: BookmarkTypes.Company,
          body: {
            data_uuid: bookmarkNoteForm?.companyId || '',
            note: get(noteBookmarkList, bookmarkNoteForm.companyId, ''),
          },
        });

    if (response?.status_code === SUCCESS_CODE.CREATE) {
      setIsBookmarkList((prev) => {
        const newIsBookmarkList = update(prev, {
          [bookmarkNoteForm?.companyId || '']: {
            $set: true,
          },
        });
        return newIsBookmarkList;
      });
    }

    handleCloseBookmarkNoteForm();
  };

  const renderLoading = () => (
    <Grid container spacing={2}>
      {Array(Number(query?.limit || LIMIT_GET_LIST))
        .fill('')
        .map((_, index) => (
          <Grid key={`skeleton-profile-company-${index}`} sm={12} md={12} lg={12} xl={6} item>
            <SkeletonProfile />
          </Grid>
        ))}
    </Grid>
  );

  const onChangeCheckbox = (company: ICompanyProfile, checked: boolean) => {
    if (size(selectedCompanies) === 3 && checked) return;
    if (checked) {
      setSelectedCompanies((prev) => [...prev, company]);
    } else {
      const state = filter(selectedCompanies, (item: ICompanyProfile) => item.id !== company.id);
      setSelectedCompanies(state);
    }
  };

  const renderCompanyComparation = () => {
    if (isEmpty(selectedCompanies)) return null;

    const onDelete = (company: ICompanyProfile) => {
      const state = filter(selectedCompanies, (item: ICompanyProfile) => item.id !== company.id);
      setSelectedCompanies(state);
    };

    return (
      <Box gap={3} alignItems={'center'} display={'flex'} p={1}>
        <Typography color={'text.secondary'} variant="subtitle2">
          Compare these profiles
        </Typography>

        <Stack flexWrap={'wrap'} direction={'row'} spacing={1}>
          {map(selectedCompanies, (company: ICompanyProfile) => (
            <Chip onDelete={() => onDelete(company)} color="secondary" key={company.id} label={company.name} />
          ))}
        </Stack>

        <ButtonWithLoading
          onClick={() => {
            if (size(selectedCompanies) < 2) return;
            let search = '';
            selectedCompanies.forEach((company) => (search += `companyIds=${company.id}&`));
            navigate({
              pathname: AuthRouteKeys.CompareCompany,
              search: search,
            });
          }}
          disabled={size(selectedCompanies) < 2}
          isRounded
          variant="contained"
        >
          Compare
        </ButtonWithLoading>
      </Box>
    );
  };

  return (
    <Container
      maxWidth="xl"
      sx={{
        margin: '0px auto',
        py: 2,
      }}
    >
      <Stack gap={2}>
        <Stack direction={'row'} flexWrap="wrap" justifyContent={'space-between'}>
          <Typography variant="h5">Company Profile Search Results</Typography>
          {/*<Typography color={palette.text.secondary} display={'flex'} variant="body2">*/}
          {/*  We found*/}
          {/*  <Typography fontWeight={600} color={'inherit'} sx={{ mx: 1 }} variant="body2">*/}
          {/*    {numeral(get(data, 'total_docs_count', 0)).format('0,0')} of{' '}*/}
          {/*    {numeral(get(data, 'total_companies_count', 0) || 0).format('0,0')} company profiles matching*/}
          {/*  </Typography>*/}
          {/*  your search conditions*/}
          {/*</Typography>*/}
        </Stack>

        <Box>
          <SectionErrorBoundary resetKey="FilterCompanyProfile" sectionName="FilterCompanyProfile">
            <FilterCompanyProfile />
          </SectionErrorBoundary>
        </Box>

        <SectionErrorBoundary resetKey="CompanyProfilesResults" sectionName="CompanyProfilesResults">
          {renderCompanyComparation()}

          {isFetching ? (
            renderLoading()
          ) : (
            <React.Fragment>
              {listCompany.length === 0 ? (
                <NoDataSearchCompany />
              ) : (
                <Grid spacing={3} container>
                  <Grid sm={12} md={8} lg={8} xl={9} item>
                    <Grid container spacing={2}>
                      {listCompany.map((company: ICompanyProfile) => {
                        const isChecked = find(selectedCompanies, (o) => o.id === company.id) ? true : false;
                        return (
                          <Grid sm={12} md={12} lg={12} xl={6} key={company.id} item>
                            <CompanyProfileCard
                              isChecked={isChecked}
                              isBookmark={checkCompanyIsBookmark(company)}
                              onChangeCheckbox={onChangeCheckbox}
                              onSelect={() => {
                                setSelected(company);
                              }}
                              selected={selected}
                              company={company}
                              handleOpenBookmarkNoteForm={handleOpenBookmarkNoteForm}
                            />
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Grid>
                  {selected && (
                    <Grid sm={12} md={4} lg={4} xl={3} item>
                      <CompanyProfileDetail
                        selected={selected}
                        isBookmark={checkCompanyIsBookmark(selected)}
                        handleOpenBookmarkNoteForm={handleOpenBookmarkNoteForm}
                      />
                    </Grid>
                  )}
                </Grid>
              )}
            </React.Fragment>
          )}
          <Grid spacing={3} container>
            {listCompany.length > 0 && (
              <Grid sm={12} md={8} lg={8} xl={9} item>
                <StyledPagination
                  total_pages={data.total_pages}
                  page={Number(query.offset) + 1}
                  onChange={function (_: ChangeEvent<unknown>, value: number): void {
                    setSearchParams((searchParams) => {
                      searchParams.set('offset', String(value - 1));
                      return searchParams;
                    });
                  }}
                />
              </Grid>
            )}
          </Grid>
          <FormBookmarkNote
            open={bookmarkNoteForm.isOpen}
            handleClose={() => {
              handleCloseBookmarkNoteForm();
              // reset old note
              updateNoteBookmark(bookmarkNoteForm?.companyBookmarkNote || '');
            }}
            handleSubmit={() => handleSubmitBookmarkNoteForm()}
            value={get(noteBookmarkList, bookmarkNoteForm.companyId, '')}
            onChange={(value) => updateNoteBookmark(value)}
            isLoading={isPendingCreateBookmark || isPendingUpdateBookmark}
          />
        </SectionErrorBoundary>
      </Stack>
    </Container>
  );
};

export default CompanyProfilePage;

const SkeletonProfile = () => {
  const theme = useTheme();
  return (
    <Box
      display={'flex'}
      flexDirection="column"
      gap={1}
      padding={1.5}
      borderRadius={theme.shape.borderRadius}
      bgcolor={theme.palette.background.default}
    >
      <Skeleton animation="wave" />
      <Skeleton animation="wave" variant="rectangular" height={24} width={60} />
      <Skeleton animation="wave" variant="rectangular" height={24} width={60} />
      <Skeleton animation="wave" variant="rectangular" height={24} width={60} />
    </Box>
  );
};
